import { useEffect, useState } from 'react';
import axios from 'axios';
import { Activity, Users, Lock } from 'lucide-react';
import AdrWhtEstimatorCard from '@/components/AdrWhtEstimatorCard';
import ValuationMatrixCard from '@/components/ValuationMatrixCard';
import NonDivValuationMatrixCard from '@/components/NonDivValuationMatrixCard';
import BenchmarkComparisonCard from '@/components/BenchmarkComparisonCard';
import SplitHistoryCard from '@/components/SplitHistoryCard';
import SpacLifecycleCard from '@/components/SpacLifecycleCard';
import PlusUpsellModal from '@/components/PlusUpsellModal';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * MassiveInsightsTab — Round 95c rewrite (Jun 2026).
 *
 * Restructured per user spec:
 *   • Top slot (passed in by StockModal) — Fund Overview / About,
 *     Tier explainer, Leveraged/Futures/ADR/BDC/CEF/REIT info cards,
 *     Issuer attribution, Massive enrichment strip, Baby-bond /
 *     Preferred-share notice. Renders FIRST so single-stock lookups
 *     lead with "what kind of asset is this" before any peer/short
 *     data noise.
 *   • ADR Withholding-Tax Estimator card (was already here in Round 95r).
 *   • Peer Group Stocks.
 *
 * Sections that USED to live here and were moved out (per user request):
 *   • Technicals / Short Interest+Float / Short Volume → Stock Metrics
 *     tab (rendered via `<TechnicalsAndShortDataCards>`).
 *   • Recent Events → Dividend Info tab, under the Streak Risk Chart
 *     (rendered via `<RecentEventsCard>`).
 *
 * Future Phase-3 additions (NOT in this commit):
 *   • Valuation Matrix (DDM target price with asset-class routing)
 *   • Historical Yield Comparison
 *   • CEF Premium/Discount + Z-Score gauge
 *   • Benchmark performance vs SPY / SCHD
 */

function SectionHeader({ icon: Icon, title, hint }) {
  return (
    <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
      <div className="flex items-center gap-1.5">
        {Icon ? <Icon className="w-4 h-4 text-fuchsia-500 dark:text-fuchsia-400" /> : null}
        <h4 className="text-[11px] font-bold uppercase tracking-widest text-fuchsia-500 dark:text-fuchsia-400">
          {title}
        </h4>
      </div>
      {hint && <span className="text-[10px] font-bold text-muted-foreground">{hint}</span>}
    </div>
  );
}

export default function MassiveInsightsTab({ ticker, stock, livePrice, onOpenTicker, onNavigateWithOrigin = null, topSlot = null, afterTopSlot = null }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin'; // PAYWALL FLIP: admin || plus
  const [upsellOpen, setUpsellOpen] = useState(false);
  const [peers, setPeers] = useState([]);
  const [parentYield, setParentYield] = useState(null);
  const [userShares, setUserShares] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!ticker) return undefined;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      axios.get(`${API}/stocks/${ticker}/peers`, { withCredentials: true })
        .catch(() => ({ data: { peers: [], parent_yield_pct: null } })),
      // Personal-position fetch keeps the ADR WHT card's "Your N
      // shares" line working when the user actually holds the ADR.
      // 404 → not held → 0.
      axios.get(`${API}/portfolio/${ticker}/my-position`, { withCredentials: true })
        .catch(() => ({ data: { shares: 0 } })),
    ]).then(([p, pos]) => {
      if (cancelled) return;
      setPeers((p.data?.peers || []).slice(0, 12));
      setParentYield(typeof p.data?.parent_yield_pct === 'number' ? p.data.parent_yield_pct : null);
      setUserShares(Number(pos?.data?.shares || pos?.data?.total_shares || 0));
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [ticker]);

  // Loading state — only block when there's no topSlot to render.
  // When topSlot is present (the common case) it can paint immediately
  // while peer fetches resolve in the background.
  if (loading && !topSlot) {
    return (
      <div className="flex items-center justify-center min-h-[200px]" data-testid="massive-insights-tab-loading">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // ADR WHT card has its own self-hide rules.
  const showWhtCard = !!(
    stock?.is_adr
    && stock?.country_code
    && Number(stock?.dividend_rate || 0) > 0
  );
  const allEmpty = !topSlot && !peers.length && !showWhtCard;
  if (allEmpty && !loading) {
    return (
      <div className="text-center py-10 text-muted-foreground text-sm" data-testid="massive-insights-tab-empty">
        <Activity className="w-8 h-8 mx-auto mb-2 opacity-40" />
        No insights available for this ticker yet.
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="massive-insights-tab">
      {/* Top slot — Fund Overview / About / asset-class explainer
          cards, rendered by StockModal so we don't have to plumb
          every nav helper through the props. */}
      {topSlot}

      {/* Round P141·5 (Feb 2026) — Warrant-only slot injected BETWEEN
          Fund Overview (topSlot) and the Valuation Matrix. Lets the
          Warrant vs Underlying compare chart + WarrantPanel P/L card
          sit right below the company summary — the user's requested
          order — without pushing the equity insights below the fold. */}
      {afterTopSlot}

      {/* Round P153 (Sep 2026) — SPAC lifecycle card. Self-fetches
          /spac-lifecycle; shows the stage stepper (Searching → Deal
          Announced → Vote Pending → Completed / Liquidated), the
          de-SPAC new-ticker link, and SEC filing citations. All SPAC
          detail lives HERE per user spec — the badge row has no room,
          especially on phones. */}
      {(stock?.is_spac || stock?.despac_predecessor) && (
        <SpacLifecycleCard ticker={ticker} onOpenTicker={onOpenTicker} onNavigate={onNavigateWithOrigin} />
      )}

      {/* Round 95c (Jun 2026) — Valuation Matrix (DDM target buy price)
          with asset-class routing. Renders for common stock, ADR, REIT,
          Preferred Share, and Baby Bond; surfaces a "DDM bypassed"
          notice for BDC / CEF where the math is mathematically wrong.

          Round 97a (Feb 2026) — Non-dividend tickers get the
          FCFE/Owner-Earnings sibling card instead. DDM with D₀=0 was
          producing a meaningless $0 target (e.g. ANF showing
          "-88.8% Overvalued" because its $0.80 forward dividend can't
          justify $89 of equity). The non-div card routes between
          Two-Stage FCFE, OCF Multiplier, and EBITDA Multiplier based
          on whichever math actually holds for that ticker.

          P158·9 (Feb 2026) — Money Market Funds SKIP the Valuation
          Matrix entirely. A $1.00 NAV product has no dividend-growth
          runway to discount, no fair value to solve for. The MMF
          Overview card in topSlot already tells the user this. */}
      {stock?.is_money_market_fund ? (
        <div
          className="rounded-lg border bg-card p-3 text-sm text-muted-foreground"
          data-testid="mmf-ddm-na"
        >
          <strong>Valuation Matrix — not applicable.</strong> Money Market Funds
          target a constant $1.00 NAV, so there is no dividend-growth stream to
          discount and no target buy price to solve for. See the MMF Overview
          card above and the Dividend Info tab for the 7-day yield and payout
          cadence.
        </div>
      ) : stock?.pays_dividend === false ? (
        <NonDivValuationMatrixCard stock={stock} livePrice={livePrice} />
      ) : (
        <ValuationMatrixCard stock={stock} livePrice={livePrice} />
      )}

      {/* Phase 4 (Jun 2026) — Benchmark performance vs SPY / SCHD / VYM
          over 1Y / 3Y / 5Y windows. DRIP-on math (every cash dividend
          rebought at that day's close). Answers the dividend investor's
          recurring "yeah but how does it stack up against just owning
          the index?" question without leaving the modal.
          Round 96g (Feb 2026) — Auto-swaps to the growth-benchmark
          trio (SPYG / QQQ / IWF) for non-dividend stocks (TXG, NFLX,
          PLTR, etc.) where comparing to dividend-quality ETFs is
          signal noise. Same card UI, different yardstick — picks the
          benchmark family that actually matches the asset profile. */}
      <BenchmarkComparisonCard
        ticker={ticker}
        mode={stock?.pays_dividend === false ? 'growth' : 'dividend'}
        peg={stock?.peg_ratio ?? stock?.equity_quality?.peg_ratio ?? null}
      />

      {/* ADR Withholding-Tax Estimator. Round 95r — first dynamic
          card after the About block. Self-hides for non-ADRs. */}
      <AdrWhtEstimatorCard stock={stock} userShares={userShares} />

      {/* Round 97l (Feb 2026) — Split History + Cost-Basis Calculator.
          Self-hides for tickers that have never split AND for
          non-paid users (the backend `/api/stocks/:t/splits` returns
          402 for non-admins so the card silently disappears). When
          the non-div paywall ships, the gate flips automatically
          via the backend check — no frontend change required. */}
      <SplitHistoryCard ticker={ticker} />

      {/* Peer Group Stocks */}
      {peers.length > 0 && (
        <div className="rounded-lg border bg-card p-3" data-testid="insights-peers-card">
          <SectionHeader
            icon={Users}
            title="Peer Group Stocks"
            hint="Click a chip to open that ticker"
          />
          <div className="flex flex-wrap gap-1.5">
            {peers.map((p) => {
              const t = p.ticker;
              const py = typeof p.yield_pct === 'number' ? p.yield_pct : null;
              // Jul 2026 (Plus paywall) — non-dividend peers (or peers
              // outside the dividend DB) are Plus-gated: gold lock,
              // click opens the upsell. Admin bypass keeps drill-through.
              const locked = p.pays_dividend !== true && !isAdmin;
              let yieldTone = 'text-muted-foreground';
              if (py != null && parentYield != null) {
                const delta = py - parentYield;
                if (delta > 0.25) yieldTone = 'text-emerald-600 dark:text-emerald-400';
                else if (delta < -0.25) yieldTone = 'text-rose-600 dark:text-rose-400';
              }
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => (locked ? setUpsellOpen(true) : onOpenTicker?.(t))}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-mono font-semibold transition-colors ${locked
                    ? 'bg-muted/60 text-muted-foreground ring-1 ring-amber-500/25 hover:bg-amber-500/10 hover:text-amber-700 dark:hover:text-amber-400'
                    : 'bg-violet-50 dark:bg-violet-950/40 text-violet-700 dark:text-violet-300 hover:bg-violet-100 dark:hover:bg-violet-900/40'}`}
                  data-testid={locked ? `insights-peer-locked-${t}` : `insights-peer-${t}`}
                  title={locked
                    ? `${t} is a non-dividend stock — unlock the full market with DiviDrip Plus`
                    : (p.name ? `${t} — ${p.name}` : `Open ${t}`)}
                >
                  <span>{t}</span>
                  {locked ? (
                    <Lock className="w-3 h-3 text-yellow-600 dark:text-yellow-400" data-testid={`insights-peer-lock-icon-${t}`} />
                  ) : py != null ? (
                    <span className={`${yieldTone} font-sans font-medium tabular-nums`}>
                      {py.toFixed(2)}%
                    </span>
                  ) : (
                    <span
                      className="text-muted-foreground/70 font-sans font-medium italic"
                      title="Not a dividend stock — not in DiviDrip's database. Click to view on-the-fly."
                    >
                      no div
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          <PlusUpsellModal open={upsellOpen} onClose={() => setUpsellOpen(false)} />
        </div>
      )}
    </div>
  );
}
